import { useAppSelector, useAppDispatch } from "store/hooks";
import { saveQuoteData, updateQuoteData, clearQuoteData, type QuoteData } from "store/application/applicationSlice";
import { useCallback, useState, useTransition, useRef } from "react";
import { getDirectTermOnlineQuote } from "services/onlineApplicationQuoteService";
import { getDirectTermPlanCode } from "domain/planCodes";
import { pushGTMEvent } from "utils/gtmEvent";
import { GA_EVENT_CONFIGS } from "constants/gaEvents";
import { getQuote as getQuoteService, storeQuote as storeQuoteService } from "services/onlineApplicationService";
import { createDate, toLocalISODateString } from "utils/age";
import { toSingleLetterFromLowerCaseGender } from "utils/toSingleLetterFromLowerCaseGender";
import { toUpperCaseGenderFromLowerCaseGender } from "utils/conversions";
import { toUsStates } from "constants/validations";
import { toSpouseApplicationId } from "mappers/transformDTOSubmissionData";
import { transformGetQuoteDataToQuoteData } from "utils/quoteUtils";
import { ROUTES } from "routes/constants";
import { useProductContext, useProductConfig } from "context/ProductContext";

export const useQuote = () => {
    const dispatch = useAppDispatch();
    const { isGIWL, isMLTA } = useProductContext();
    const productConfig = useProductConfig();
    const quoteData = useAppSelector((state) => state.application.quoteData);
    const offerData = useAppSelector((state) => state.application.offerData);
    const saveContId = useAppSelector((state) => state.application.saveContId);
    const [isLoadingQuotes, startQuoteTransition] = useTransition();
    const [quoteError, setQuoteError] = useState<string | null>(null);

    const quoteDataRef = useRef(quoteData);
    const offerDataRef = useRef(offerData);
    const saveContIdRef = useRef(saveContId);
    quoteDataRef.current = quoteData;
    offerDataRef.current = offerData;
    saveContIdRef.current = saveContId;

    const saveQuote = useCallback(
        (data: QuoteData) => {
            dispatch(saveQuoteData(data));
        },
        [dispatch]
    );

    const clearQuote = useCallback(() => {
        dispatch(clearQuoteData());
    }, [dispatch]);

    const saveCoverageSelection = useCallback(
        (selectedCoverageAmount: number, selectedCoveragePremium: number) => {
            dispatch(
                updateQuoteData({
                    selectedCoverageAmount,
                    selectedCoveragePremium,
                })
            );
        },
        [dispatch]
    );

    const storeQuoteFromData = useCallback(
        async (data: QuoteData): Promise<boolean> => {
            const currentOfferData = offerDataRef.current;
            const currentSaveContId = saveContIdRef.current;

            if (!data || (!isGIWL && !currentOfferData)) {
                console.warn("Cannot store quote: missing quote or offer data");
                return false;
            }

            if (isGIWL && !currentSaveContId) {
                console.warn("Cannot store quote: missing saveContId for GIWL");
                return false;
            }

            try {
                const birthDateObj = createDate(
                    parseInt(data.dobYear, 10),
                    parseInt(data.dobMonth, 10),
                    parseInt(data.dobDay, 10)
                );

                const userRef = isGIWL
                    ? currentSaveContId!
                    : data.applicantType === "member"
                    ? currentOfferData!.invitationCode
                    : toSpouseApplicationId(currentOfferData!.invitationCode);

                const requestBody = {
                    userRef,
                    productCode: productConfig.productCode,
                    dateOfBirth: toLocalISODateString(birthDateObj),
                    gender: data.gender ? toSingleLetterFromLowerCaseGender(data.gender) : undefined,
                    state: toUsStates(data.state),
                    hasUsedNicotine: data.hasUsedNicotineLastYear,
                    coverageAmount: data.selectedCoverageAmount ?? 0,
                    coveragePlan: (isMLTA ? data.selectedTier : data.planCode) ?? undefined,
                    includeFamilyCoverage:
                        data.selectedCoverageType === "Family"
                            ? true
                            : data.selectedCoverageType === "Individual"
                            ? false
                            : undefined,
                    premium: data.selectedCoveragePremium ?? 0,
                };

                return await storeQuoteService(requestBody);
            } catch (error) {
                console.warn("Error preparing quote:", error);
                return false;
            }
        },
        [isGIWL, isMLTA, productConfig]
    );

    const storeQuote = useCallback(async () => {
        const currentQuoteData = quoteDataRef.current;

        if (!currentQuoteData) {
            console.warn("Cannot store quote: missing quote or offer data");
            return false;
        }
        return await storeQuoteFromData(currentQuoteData);
    }, [storeQuoteFromData]);

    const fetchCoverageOptions = useCallback(
        (params: {
            state: string;
            gender: "male" | "female";
            hasUsedNicotineLastYear: boolean;
            dobMonth: string;
            dobDay: string;
            dobYear: string;
            offerCoverageAmounts?: number[];
        }) => {
            setQuoteError(null);

            return new Promise<{
                success: boolean;
                coverageOptions?: Array<{ coverageAmount: number; monthlyPremium: number }>;
                route?: string;
            }>((resolve) => {
                startQuoteTransition(async () => {
                    try {
                        const planCode = getDirectTermPlanCode(toUsStates(params.state)!);

                        const response = await getDirectTermOnlineQuote({
                            state: toUsStates(params.state)!,
                            zipCode: undefined,
                            gender: toUpperCaseGenderFromLowerCaseGender(params.gender),
                            useNicotine: params.hasUsedNicotineLastYear,
                            year: params.dobYear,
                            month: params.dobMonth,
                            day: params.dobDay,
                            productCode: planCode,
                        });

                        const quotes = response.quotes?.flatMap((quote) =>
                            Object.values(quote).every((value) => typeof value !== "undefined")
                                ? [quote as Required<typeof quote>]
                                : []
                        );

                        if (!quotes || quotes.length < 1) {
                            pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_INVALID);

                            resolve({ success: false, route: ROUTES.SOMETHING_WENT_WRONG });
                            return;
                        }

                        let coverageOptions = quotes.map((quote) => ({
                            coverageAmount: quote.coverageAmount,
                            monthlyPremium: quote.totalPremiumAmount,
                        }));

                        // Filter coverage options to only include amounts from offer data if provided
                        if (params.offerCoverageAmounts && params.offerCoverageAmounts.length > 0) {
                            coverageOptions = coverageOptions.filter((option) =>
                                params.offerCoverageAmounts!.includes(option.coverageAmount)
                            );
                        }

                        resolve({ success: true, coverageOptions });
                    } catch (error) {
                        console.error("Error fetching coverage options:", error);
                        setQuoteError(error instanceof Error ? error.message : "Failed to fetch quotes");

                        pushGTMEvent(GA_EVENT_CONFIGS.QUOTE_FETCH_ERROR);
                        resolve({ success: false, route: ROUTES.SOMETHING_WENT_WRONG });
                    }
                });
            });
        },
        [startQuoteTransition]
    );

    /**
     * Loads saved quote data from previous sessions/backend
     * If quote found, saves it to Redux store
     */
    const loadSavedQuoteData = useCallback(
        (invitationCode?: string, applicantType?: "member" | "spouse", offerCoverageAmounts?: number[]): Promise<void> => {
            const currentSaveContId = saveContIdRef.current;

            if (isGIWL) {
                if (!currentSaveContId) return Promise.resolve();

                return new Promise<void>((resolve) => {
                    startQuoteTransition(async () => {
                        try {
                            const savedQuote = await getQuoteService(currentSaveContId);

                            if (Object.keys(savedQuote).length === 0) return;

                            const quoteData = transformGetQuoteDataToQuoteData("member", savedQuote, []);
                            saveQuote(quoteData);
                        } catch (error) {
                            // Silent failure
                        } finally {
                            resolve();
                        }
                    });
                });
            }

            if (!applicantType || !invitationCode || !offerCoverageAmounts) {
                return Promise.resolve();
            }

            return new Promise<void>((resolve) => {
                startQuoteTransition(async () => {
                    try {
                        const quoteInvitationCode =
                            applicantType === "member" ? invitationCode : toSpouseApplicationId(invitationCode);

                        const savedQuote = await getQuoteService(quoteInvitationCode);

                        if (Object.keys(savedQuote).length === 0) {
                            return;
                        }

                        const quoteData = transformGetQuoteDataToQuoteData(applicantType, savedQuote, offerCoverageAmounts);
                        saveQuote(quoteData);
                    } catch (error) {
                        // Silent failure - this is expected for new applications or 404s
                    } finally {
                        resolve();
                    }
                });
            });
        },
        [isGIWL, saveQuote, startQuoteTransition]
    );

    return {
        loadSavedQuoteData,
        quoteData,
        saveQuote,
        clearQuote,
        saveCoverageSelection,
        fetchCoverageOptions,
        storeQuote,
        storeQuoteFromData,
        isLoadingQuotes,
        quoteError,
    };
};
